import { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import RelativeLink from "app/pages/.shared/RelativeLink";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import Paragraphs from "app/pages/.shared/Paragraphs";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import "./OneMerchBanner.scss";
import { RESOLUTION } from "app/constants";
import { stringify } from "qs";

const IMG_SIZES = {
	small: { width: 600 },
	medium: { width: 600 },
	large: { width: 350, height: 196 },
	xlarge: { width: 350, height: 196 },
};

const OneMerchBanner = ({ resolution, title = [], merchandising = {} }) => {
	const { track } = useAnalytics();
	const history = useHistory();

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const imageUrl = isMobile ? merchandising.photoSmall?.url : merchandising.photoLarge?.url;

	const thumbnail = isMobile
		? merchandising.photoSmall?.thumbnail
		: merchandising.photoLarge?.thumbnail;

	const buttonLabel = merchandising.displayOptions?.labelCta;

	const to = merchandising.displayOptions?.redirectUrl
		? {
				pathname: merchandising.displayOptions?.redirectUrl,
		  }
		: {
				pathname: "/merch",
				search: `?${stringify({ code: merchandising.code })}`,
		  };

	const merchIntro = merchandising.displayOptions?.marketingDescription;

	let merchDesktopTitle = merchandising.displayOptions?.smartDP?.merchandisingTitleDesktop;
	const merchDesktopSubtitle =
		merchandising.displayOptions?.smartDP?.merchandisingSubtitleDesktop;
	if (merchDesktopSubtitle) {
		merchDesktopTitle = `${merchDesktopTitle}, ${merchDesktopSubtitle.toLowerCase()}`;
	}
	let merchMobileTitle = merchandising.displayOptions?.smartDP?.merchandisingTitleMobile;
	const merchMobileSubtitle = merchandising.displayOptions?.smartDP?.merchandisingSubtitleMobile;

	if (merchMobileSubtitle) {
		merchMobileTitle = `${merchMobileTitle}, ${merchMobileSubtitle.toLowerCase()}`;
	}

	const merchTitle = isMobile ? merchMobileTitle : merchDesktopTitle;

	const handleCTAClick = useCallback(() => {
		track("one-merch-banner_block_cta_click", {
			ctaLabel: buttonLabel,
			title: title.join(","),
			merchtitle: merchTitle,
		});
	}, [buttonLabel, title, merchTitle]);

	const handleOneMerchClick = useCallback(() => {
		handleCTAClick();
		history.push(to);
	}, [to.pathname]);

	return (
		<div className="one-merch-banner">
			{title?.length > 0 && (
				<h3 className="one-merch-banner__title">
					<Paragraphs paragraphs={title} enableHTMLFormatting />
				</h3>
			)}
			<div className="one-merch-banner__wrapper">
				{imageUrl && (
					<div className="one-merch-banner__image" onClick={handleOneMerchClick}>
						<ProgressiveImg
							sizes={IMG_SIZES}
							resolution={resolution}
							src={imageUrl}
							thumbnail={thumbnail}
						/>
					</div>
				)}
				<div className="one-merch-banner__container">
					{merchTitle?.length > 0 && (
						<h4 className="one-merch-banner__merch-title">
							<Paragraphs paragraphs={merchTitle} enableHTMLFormatting />
						</h4>
					)}
					<article className="one-merch-banner__merch-intro">
						<Paragraphs paragraphs={merchIntro} enableHTMLFormatting />
					</article>
					{buttonLabel && (
						<RelativeLink
							to={to}
							onClick={handleCTAClick}
							className="one-merch-banner__cta"
							variant="secondary"
							data-testid="reinsurance-cta"
						>
							{buttonLabel}
						</RelativeLink>
					)}
				</div>
			</div>
		</div>
	);
};

OneMerchBanner.propTypes = {
	resolution: PropTypes.string,
	title: PropTypes.array,
	merchandising: PropTypes.object,
};

export default memo(OneMerchBanner);
